/*
    Deployment settings, read at boot from /config.json.

    Nothing about a particular company or mail server is compiled in. Whoever runs this edits one
    JSON file next to index.html and reloads — no build step, no toolchain, no Node on their
    machine. That matters because this is handed over rather than hosted: the person changing the
    name on the login page should not need the source.

    Anything missing from the file falls back to the defaults below, and a missing file is fine —
    that is simply "the defaults", which is a working setup.
*/

export type Language = 'tr' | 'en';

/*
    Four identities meet on this screen, and until now one field carried all of them.

      the product   — summail web, what this software is
      the operator  — the company whose address was typed to get here
      the mailbox   — the person's own account
      the provider  — Makrosum, who runs it

    `productName` was being set to the operator's name by every branded address, so the screen
    said "Rapor Ekranı Posta" while the mailbox was info@patronekrani.com and nothing explained
    how the two were related — or where the product had gone. Somebody looking at it could not
    tell which word was the software, which was their company, and which was them.

    So the operator gets its own fields. The product name is the product's; an operator supplies a
    logo and a name, and the interface says which is which.
*/
export interface AppConfig {
    /** The software. summail web, everywhere, whoever the deployment is for. */
    productName: string;
    /** Paths or URLs to the product's mark. Falls back to the built-in envelope when null. */
    logo: string | null;
    logoDark: string | null;
    /**
     * The company this address belongs to, when it is not us.
     *
     * Shown beside the product rather than instead of it: "summail web · Rapor Ekranı için". Null
     * on our own addresses, where there is no second party and saying so would be noise.
     */
    operatorName: string | null;
    operatorLogo: string | null;
    operatorLogoDark: string | null;
    /**
     * The one mail domain this address serves, when it serves one.
     *
     * Declared rather than taken from the host: they are not the same thing. Somebody signed in to
     * info@patronekrani.com through webmail.raporekrani.com, and a deployment that guessed would
     * have sent them to a mailbox that does not exist. Null means the sign-in field asks for a
     * whole address, which is what a multi-domain address must do.
     */
    mailDomain: string | null;
    /** Which language the interface opens in. Whoever is using it can still switch. */
    defaultLanguage: Language;
    /** Brand colours, as CSS custom properties. Applied under the user's own edits. */
    tokens: Record<string, string>;
    /** Addresses offered as one-tap hints on the sign-in screen. */
    suggestedMailboxes: { address: string; note?: string }[];
    /** Who to contact when signing in does not work. Shown on the sign-in card. */
    supportText: string;
    supportUrl: string;
    /*
        The mail server, for the "how do I set this up on my phone" guide.

        Answered by the backend rather than compiled in, for the same reason as everything else
        here: this is handed over, and the deployment knows its own server. Empty means the guide
        leaves the manual-settings table out entirely rather than printing blanks.
    */
    imapAddr: string;
    smtpAddr: string;
    /*
        This operator's own Android app, if there is one. A deployment with no app draws no
        Android section — absent rather than a dead button.
    */
    androidAppUrl: string;
}

/*
    Two settings from the version this came from are deliberately gone.

    `serverBase` pointed the app at a mail server on another host. It cannot: the session is a
    cookie, a cookie belongs to one origin, and the backend serves the page itself so that there
    is only ever one. A setting that could only break things is worse than no setting.

    `showServerLoginFallback` linked to the mail server's own sign-in page. Dovecot has no such
    page — there is nothing on the other end of that link.
*/

export const DEFAULTS: AppConfig = {
    productName: 'summail web',
    logo: '/summail-sembol.svg',
    logoDark: '/summail-sembol-beyaz.svg',
    operatorName: null,
    operatorLogo: null,
    operatorLogoDark: null,
    mailDomain: null,
    // Turkish, because that is who this is for. A browser set to English still shows English:
    // this is only the starting point, and the switch is on the sign-in card.
    defaultLanguage: 'tr',
    tokens: {},
    suggestedMailboxes: [],
    supportText: '',
    supportUrl: '',
    imapAddr: '',
    smtpAddr: '',
    androidAppUrl: '',
};

let current: AppConfig = DEFAULTS;

export const config = (): AppConfig => current;

function coerce(raw: unknown): AppConfig {
    const p = (raw ?? {}) as Partial<AppConfig>;
    return {
        ...DEFAULTS,
        ...p,
        defaultLanguage: p.defaultLanguage === 'en' ? 'en' : DEFAULTS.defaultLanguage,
        tokens: p.tokens ?? {},
        suggestedMailboxes: Array.isArray(p.suggestedMailboxes) ? p.suggestedMailboxes : [],
    };
}

/** Call once, before the first render. */
export async function loadConfig(): Promise<AppConfig> {
    try {
        // no-store rather than no-cache: a stale config is how somebody changes a setting,
        // reloads, and sees nothing happen.
        const res = await fetch('/config.json', { cache: 'no-store', headers: { accept: 'application/json' } });
        if (res.ok) current = coerce(await res.json());
    } catch {
        /* no config file, or one that will not parse — the defaults are a working setup */
    }
    if (typeof document !== 'undefined') document.title = current.productName;
    return current;
}
